import React, { useState, useRef, useEffect } from 'react'
import { toast } from 'react-toastify'

import {
  Icon,
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter
} from '@clientbase/clientbase-library'
import { ThumbDown, ThumbUp } from '@mui/icons-material'
import { Stack, CircularProgress, Box, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import useTypingEffect from 'hooks/useTypingEffect'
import Welcome from 'templates/Overview/components/Welcome'

import IconContainer from 'components/IconContainer/IconContainer'
import ModalNewCharge from 'components/Modal/ModalNewCharge'
import ModalNewRecurrence from 'components/Modal/ModalNewRecurrence'

import { fetchReportData } from 'utils/reports/fetchReportData'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import * as S from './ChatAssistant.styles'
import CSVModal from './CSVModal'
import { parseUiAction, useChatUiAction } from './hooks/useChatUiAction'

interface Message {
  id: string
  text: string
  isUser: boolean
  timestamp: Date
  feedback?: 0 | 1 | 2
}

interface ChatAssistantProps {
  isModalOpen?: boolean
  onCloseModal?: () => void
}

const AILoadingIndicator: React.FC = () => {
  const loadingPhrases = [
    'Analisando sua solicitação...',
    'Consultando dados do seu negócio...',
    'Processando informações...',
    'Buscando insights relevantes...',
    'Preparando sua resposta...',
    'Organizando os resultados...',
    'Verificando os detalhes...',
    'Quase pronto...',
    'Finalizando a análise...'
  ]

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0)
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false)

      setTimeout(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % loadingPhrases.length)
        setIsVisible(true)
      }, 300)
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  return (
    <S.AILoaderContainer>
      <S.AIAvatarLoader>
        <Icon
          icon="autoAwesome"
          size={2.5}
          colorSX={theme.palette.primary.main}
        />
      </S.AIAvatarLoader>
      <S.LoaderContent>
        <S.LoaderText isVisible={isVisible}>
          {loadingPhrases[currentPhraseIndex]}
        </S.LoaderText>
        <S.DotsContainer>
          <S.Dot delay={0} />
          <S.Dot delay={150} />
          <S.Dot delay={300} />
        </S.DotsContainer>
      </S.LoaderContent>
    </S.AILoaderContainer>
  )
}

const isReportLink = (url: string): boolean => {
  // Verifica se é um link de report da API
  return url.includes('/v1/reports/') && url.includes('/excel')
}

const parseMessage = (
  text: string,
  onOpenReportModal?: (url: string) => void
): React.ReactNode => {
  try {
    // Validar se o texto é uma string válida
    if (!text || typeof text !== 'string') {
      return 'Mensagem inválida'
    }

    // Primeiro, verificar se há bullet lists e processar o texto por blocos
    const hasbulletLists = /^\s*\*\s+/m.test(text)

    if (hasbulletLists) {
      return processBulletLists(text, onOpenReportModal)
    }

    // Se não há bullet lists, processar normalmente
    return processTextWithFormatting(text, onOpenReportModal)
  } catch (error) {
    console.error('Error parsing message:', error)
    return text || 'Erro ao processar mensagem'
  }
}

const processBulletLists = (
  text: string,
  onOpenReportModal?: (url: string) => void
): React.ReactNode => {
  try {
    const lines = text.split('\n')
    const result: React.ReactNode[] = []
    let currentList: string[] = []
    let listIndex = 0

    const flushCurrentList = () => {
      if (currentList.length > 0) {
        result.push(
          <ul
            key={`list-${listIndex++}`}
            style={{ margin: '8px 0', paddingLeft: '20px' }}
          >
            {currentList.map((item, index) => (
              <li key={index} style={{ marginBottom: '4px' }}>
                {processTextWithFormatting(item, onOpenReportModal)}
              </li>
            ))}
          </ul>
        )
        currentList = []
      }
    }

    lines.forEach((line, index) => {
      try {
        const bulletMatch = line.match(/^\s*\*\s+(.+)$/)

        if (bulletMatch) {
          // É um item de lista
          currentList.push(bulletMatch[1])
        } else {
          // Não é um item de lista
          flushCurrentList()

          if (line.trim()) {
            result.push(
              <div key={`text-${index}`} style={{ marginBottom: '8px' }}>
                {processTextWithFormatting(line, onOpenReportModal)}
              </div>
            )
          } else {
            // Linha vazia
            result.push(<br key={`br-${index}`} />)
          }
        }
      } catch (lineError) {
        console.error('Error processing line:', lineError)
        result.push(<div key={`error-${index}`}>{line}</div>)
      }
    })

    // Flush any remaining list
    flushCurrentList()

    return result
  } catch (error) {
    return <div>{text}</div>
  }
}

const processTextWithFormatting = (
  text: string,
  onOpenReportModal?: (url: string) => void
): React.ReactNode => {
  try {
    // Validar input
    if (!text || typeof text !== 'string') {
      return text || ''
    }

    // Primeiro, processar bold para identificar seções em negrito
    const boldRegex = /\*\*(.+?)\*\*/g
    const boldMatches = Array.from(text.matchAll(boldRegex))

    // Se não há bold, processar normalmente
    if (boldMatches.length === 0) {
      return processLinksInText(text, onOpenReportModal)
    }

    // Se há bold, processar cada seção
    const parts: React.ReactNode[] = []
    let currentIndex = 0

    boldMatches.forEach((boldMatch) => {
      const boldStart = boldMatch.index ?? 0
      const boldEnd = boldStart + boldMatch[0].length
      const boldContent = boldMatch[1]

      // Adicionar texto antes do bold
      if (currentIndex < boldStart) {
        const textBefore = text.slice(currentIndex, boldStart)
        parts.push(...processLinksInText(textBefore, onOpenReportModal))
      }

      // Processar links dentro do texto em negrito
      const processedBoldContent = processLinksInText(
        boldContent,
        onOpenReportModal
      )

      // Envolver em strong
      parts.push(
        <strong key={`bold-${boldStart}`}>{processedBoldContent}</strong>
      )

      currentIndex = boldEnd
    })

    // Adicionar texto restante
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex)
      parts.push(...processLinksInText(remainingText, onOpenReportModal))
    }

    return parts.length > 0 ? parts : text
  } catch (error) {
    console.error('Error in processTextWithFormatting:', error)
    return text
  }
}

const processLinksInText = (
  text: string,
  onOpenReportModal?: (url: string) => void
): React.ReactNode[] => {
  try {
    // Regex para encontrar links com formato [texto](url)
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g
    // Regex para encontrar URLs diretas
    const directUrlRegex = /(https?:\/\/[^\s)]+)/g

    const parts: React.ReactNode[] = []
    let currentIndex = 0
    const allMatches: Array<{
      start: number
      end: number
      element: React.ReactNode
      type: string
    }> = []

    // Processar links markdown [texto](url)
    const markdownMatches = Array.from(text.matchAll(markdownLinkRegex))
    markdownMatches.forEach((match) => {
      const linkText = match[1]
      const url = match[2]
      const start = match.index ?? 0
      const end = start + match[0].length

      const isReport = isReportLink(url)

      allMatches.push({
        start,
        end,
        element: (
          <a
            key={`markdown-link-${start}`}
            href={isReport ? '#' : url}
            onClick={
              isReport
                ? (e) => {
                    e.preventDefault()
                    onOpenReportModal?.(url)
                  }
                : undefined
            }
            style={{
              fontWeight: 600,
              fontSize: '14px',
              color: isReport ? '#00D4AA' : undefined,
              cursor: 'pointer'
            }}
          >
            {linkText}
          </a>
        ),
        type: 'markdown-link'
      })
    })

    // Encontrar URLs diretas
    const directUrlMatches = Array.from(text.matchAll(directUrlRegex))
    directUrlMatches.forEach((match) => {
      const start = match.index ?? 0
      const end = start + match[0].length
      const url = match[0]

      // Verificar se não está dentro de um link markdown
      const isInsideMarkdownLink = allMatches.some(
        (existingMatch) =>
          existingMatch.type === 'markdown-link' &&
          start >= existingMatch.start &&
          end <= existingMatch.end
      )

      // Verificar se não é parte do formato markdown [texto](url)
      const isPartOfMarkdownFormat =
        text.substring(Math.max(0, start - 50), start).includes('[') &&
        text.substring(end, Math.min(text.length, end + 2)).includes(')')

      if (!isInsideMarkdownLink && !isPartOfMarkdownFormat) {
        const displayUrl = url.length > 50 ? url.substring(0, 47) + '...' : url
        const isReport = isReportLink(url)

        allMatches.push({
          start,
          end,
          element: (
            <a
              key={`direct-link-${start}`}
              href={isReport ? '#' : url}
              onClick={
                isReport
                  ? (e) => {
                      e.preventDefault()
                      onOpenReportModal?.(url)
                    }
                  : undefined
              }
              style={{
                fontWeight: 600,
                fontSize: '14px',
                color: isReport ? '#00D4AA' : undefined,
                cursor: 'pointer'
              }}
              title={url}
            >
              {displayUrl}
            </a>
          ),
          type: 'direct-link'
        })
      }
    })

    // Ordenar matches por posição
    allMatches.sort((a, b) => a.start - b.start)

    // Construir resultado
    allMatches.forEach((match) => {
      if (currentIndex < match.start) {
        const textBefore = text.slice(currentIndex, match.start)
        parts.push(...processLineBreaks(textBefore, currentIndex))
      }

      parts.push(match.element)
      currentIndex = match.end
    })

    // Adicionar texto restante
    if (currentIndex < text.length) {
      const remainingText = text.slice(currentIndex)
      parts.push(...processLineBreaks(remainingText, currentIndex))
    }

    return parts.length > 0 ? parts : [text]
  } catch (error) {
    return [text]
  }
}

const processLineBreaks = (
  text: string,
  baseIndex: number
): React.ReactNode[] => {
  if (!text.includes('\n')) {
    return [text]
  }

  const parts: React.ReactNode[] = []
  const lines = text.split('\n')

  lines.forEach((line, index) => {
    if (index > 0) {
      parts.push(<br key={`br-${baseIndex + index}`} />)
    }
    if (line) {
      parts.push(line)
    }
  })

  return parts
}

const ChatAssistant: React.FC<ChatAssistantProps> = ({
  isModalOpen: externalIsModalOpen,
  onCloseModal
}) => {
  const [internalIsModalOpen, setInternalIsModalOpen] = useState(false)
  const isModalOpen =
    externalIsModalOpen !== undefined
      ? externalIsModalOpen
      : internalIsModalOpen
  const setIsModalOpen =
    externalIsModalOpen !== undefined
      ? onCloseModal ||
        (() => {
          /* noop */
        })
      : setInternalIsModalOpen
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const uiAction = useChatUiAction()
  const [csvModalOpen, setCsvModalOpen] = useState(false)
  const [csvUrl, setCsvUrl] = useState('')
  const [reportLoading, setReportLoading] = useState(false)

  const [feedbackLoading, setFeedbackLoading] = useState<
    Record<string, boolean>
  >({})

  // Load messages from localStorage on component mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('chat-assistant-history')
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages)
        setMessages(parsedMessages)
      } catch (error) {
        console.warn('Failed to load chat history from localStorage:', error)
      }
    }
  }, [])

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('chat-assistant-history', JSON.stringify(messages))
    }
  }, [messages])
  const [isLoading, setIsLoading] = useState(false)
  const [showScrollDown, setShowScrollDown] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesAreaRef = useRef<HTMLDivElement>(null)

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const [dockBottom, setDockBottom] = useState(false)
  const [atMax, setAtMax] = useState(false)

  const adjustInputHeight = () => {
    const el = textAreaRef.current
    if (!el) return
    const max = 360
    const base = 36
    el.style.height = 'auto'
    const sc = el.scrollHeight
    const next = Math.max(base, Math.min(sc, max))
    el.style.height = `${next}px`
    setDockBottom(sc > base)
    setAtMax(sc >= max)
  }

  useEffect(() => {
    adjustInputHeight()
  }, [message, isModalOpen])

  const searchPhrases = [
    'Qual a minha inadimplência?',
    'Visão geral do meu negócio.',
    'Qual o meu MRR?',
    'Quais são os principais insights do meu negócio?',
    'Quais contratos estão próximos de vencer?',
    'Tenho recorrências próximas de finalizar?'
  ]

  const typingText = useTypingEffect({
    phrases: searchPhrases,
    typingSpeed: 40,
    deletingSpeed: 40,
    pauseDuration: 1500
  })

  const handleMessagesScroll = () => {
    const el = messagesAreaRef.current
    if (!el) return
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
    setShowScrollDown(!atBottom)
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollToTop = () => {
    messagesAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleOpenReportModal = async (reportUrl: string) => {
    setReportLoading(true)

    try {
      const csvUrl = await fetchReportData(reportUrl)
      if (csvUrl) {
        setCsvUrl(csvUrl)
        setCsvModalOpen(true)
      }
    } finally {
      setReportLoading(false)
    }
  }

  const handleCloseCSVModal = () => {
    setCsvModalOpen(false)
    setCsvUrl('')
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (isModalOpen) {
      const id = requestAnimationFrame(() => {
        if (messages.length === 0) {
          scrollToTop()
        } else {
          scrollToBottom()
        }
      })
      return () => cancelAnimationFrame(id)
    }
    return undefined
  }, [isModalOpen])

  // Foca o input quando o modal abrir para permitir ESC sem clicar
  useEffect(() => {
    if (!isModalOpen) return
    const id = requestAnimationFrame(() => {
      textAreaRef.current?.focus()
    })
    return () => cancelAnimationFrame(id)
  }, [isModalOpen])

  useEffect(() => {
    handleMessagesScroll()
  }, [messages, isLoading])

  const handleSendMessage = async (directMessage?: string) => {
    const messageToSend = directMessage || message
    if (!messageToSend.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageToSend,
      isUser: true,
      timestamp: new Date()
    }

    setMessages((prev) => [...prev, userMessage])
    if (isModalOpen) {
      setTimeout(scrollToBottom, 0)
    }
    const currentMessage = messageToSend
    setMessage('')
    setIsLoading(true)
    if (externalIsModalOpen === undefined) {
      setIsModalOpen(true)
    }

    try {
      // Direct call to N8N webhook using DataService
      const response = await DataService({
        type: 'POST',
        url: '/v1/agent/chat',
        data: {
          inputMessage: currentMessage,
          sessionId: 'teste',
          action: 'sendMessage'
        }
      })

      if (response.error) {
        throw new Error(
          response.message || response.error || 'Erro na resposta da API'
        )
      }

      const output = response.data?.output || response.output

      // SC-10920: o agente pode pedir para abrir um formulário já preenchido.
      // O bloco sai do texto exibido e vira a abertura do modal.
      const { text: visibleText, action } = parseUiAction(output)
      if (action) uiAction.openFromAction(action)

      const aiMessage: Message = {
        id: response.data.agent_message_uuid,
        text: visibleText,
        isUser: false,
        timestamp: new Date()
      }
      setMessages((prev) => [...prev, aiMessage])
      if (isModalOpen) {
        setTimeout(scrollToBottom, 0)
      }
      setIsLoading(false)
    } catch (error: any) {
      console.error('Erro ao enviar mensagem:', error)

      let errorText =
        'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.'

      // Check for specific error types
      if (error.message?.includes('aguarde um instante')) {
        errorText =
          'Por favor, aguarde um momento antes de enviar outra mensagem.'
      } else if (error.message?.includes('timeout')) {
        errorText =
          'A operação demorou muito tempo para processar. Isso pode acontecer com relatórios grandes ou operações complexas. Por favor, tente novamente ou simplifique sua solicitação.'
      } else if (error.detail_error) {
        errorText = error.detail_error
      }

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: errorText,
        isUser: false,
        timestamp: new Date()
      }
      setMessages((prev) => [...prev, errorMessage])
      if (isModalOpen) {
        setTimeout(scrollToBottom, 0)
      }
      setIsLoading(false)
    }
  }

  const handleQuickAction = (phrase: string) => {
    handleSendMessage(phrase)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!isLoading) {
        handleSendMessage()
      }
    }
  }

  const FEEDBACK = { like: 1 as const, dislike: 2 as const }

  const sendFeedback = async (messageId: string, feedback: number) => {
    const response = await DataService({
      url: `/v1/agent_messages/${messageId}/feedback`,
      type: 'PATCH',
      data: { feedback }
    })

    if (response?.error) {
      throw new Error(
        response?.detail_error || response?.message || 'Erro ao enviar feedback'
      )
    }

    return response
  }

  const toggleFeedback = async (messageId: string, value: 1 | 2) => {
    if (feedbackLoading[messageId]) return

    const current = (messages.find((m) => m.id === messageId)?.feedback ??
      0) as 0 | 1 | 2
    const next: 0 | 1 | 2 = current === value ? 0 : value

    setFeedbackLoading((prev) => ({ ...prev, [messageId]: true }))
    try {
      await sendFeedback(messageId, next)
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m
          const updated = { ...m } as any
          if (next === 0) delete updated.feedback
          else updated.feedback = next
          return updated
        })
      )
    } catch (e) {
      //
    } finally {
      setFeedbackLoading((prev) => ({ ...prev, [messageId]: false }))
    }
  }

  const handleLike = async (msg: Message) => {
    await toggleFeedback(msg.id, FEEDBACK.like)
  }

  const handleDislike = async (msg: Message) => {
    await toggleFeedback(msg.id, FEEDBACK.dislike)
  }

  const handleCopy = async (msg: Message) => {
    try {
      await navigator.clipboard.writeText(msg.text)
      toast.success('Mensagem copiada', toastProps)
    } catch (err) {
      toast.error('Não foi possível copiar', toastProps)
    }
  }

  return (
    <>
      {!isModalOpen && (
        <S.Container>
          <Welcome />

          <Stack
            my={2}
            direction="row"
            gap={2}
            flexWrap="wrap"
            alignItems="center"
            justifyContent="center"
          >
            {searchPhrases.slice(0, 3).map((suggestion) => (
              <Button
                key={suggestion}
                variantButton="primary"
                buttonSize="md"
                endIcon={'autoAwesome'}
                onClick={() => handleQuickAction(suggestion)}
                sx={{
                  background: 'rgba(220, 220, 220, 0.07)',
                  backdropFilter: 'blur(15px)',
                  WebkitBackdropFilter: 'blur(15px)',
                  borderRadius: '999px',
                  boxShadow: '0px 1.27px 15.27px 0px rgba(0, 0, 0, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.4)',

                  minWidth: 'fit-content',
                  [theme.breakpoints.down(500)]: {
                    width: '100%'
                  },
                  ':hover': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    boxShadow: '0 2px 20px 0 rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                {suggestion}
              </Button>
            ))}
          </Stack>

          <S.InputWrapper>
            <S.InputContainer stack={dockBottom}>
              <Icon icon="autoAwesome" colorSX={theme.palette.primary.main} />
              <S.ChatInput
                ref={textAreaRef}
                rows={1}
                placeholder={typingText}
                value={message}
                paddedForButton={!dockBottom}
                atMax={atMax}
                onInput={adjustInputHeight}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setMessage(e.target.value)
                }
                onKeyDown={handleKeyDown}
              />

              {!dockBottom && (
                <S.SendButton
                  overlay
                  onClick={() => handleSendMessage()}
                  disabled={!message.trim()}
                  hasMessage={!!message.trim()}
                  variantButton="primary"
                >
                  <Icon
                    icon="faArrowRight"
                    size={2}
                    colorSX={theme.palette.secondary[100]}
                  />
                </S.SendButton>
              )}

              {dockBottom && (
                <S.ButtonRow>
                  <S.SendButton
                    onClick={() => handleSendMessage()}
                    disabled={!message.trim()}
                    hasMessage={!!message.trim()}
                    variantButton="primary"
                  >
                    <Icon
                      icon="faArrowRight"
                      size={2}
                      colorSX={theme.palette.secondary[100]}
                    />
                  </S.SendButton>
                </S.ButtonRow>
              )}
            </S.InputContainer>
          </S.InputWrapper>
        </S.Container>
      )}

      <Modal
        open={isModalOpen}
        handleClose={() =>
          externalIsModalOpen !== undefined
            ? onCloseModal?.()
            : setIsModalOpen(false)
        }
        width="90vw"
        maxWidth="1200px"
        height="90vh"
      >
        <ModalHeader
          onClose={() =>
            externalIsModalOpen !== undefined
              ? onCloseModal?.()
              : setIsModalOpen(false)
          }
        >
          <S.AssistantHeader>
            <IconContainer
              icon="autoAwesome"
              color={theme.palette.primary.main}
            />
            <S.AssistantInfo>
              <Typography variant="mdBold">
                Assistente Base de Clientes
              </Typography>
              <S.AssistantStatus>
                <S.StatusDot isActive={!isLoading} />
                <span>{isLoading ? 'Digitando...' : 'Online'}</span>
              </S.AssistantStatus>
            </S.AssistantInfo>
          </S.AssistantHeader>
        </ModalHeader>

        <ModalContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 0,
            position: 'relative'
          }}
        >
          <S.MessagesArea ref={messagesAreaRef} onScroll={handleMessagesScroll}>
            <S.WelcomeHeader hasMessages={messages.length > 0}>
              <S.EmptyStateIcon>
                <Icon
                  icon="autoAwesome"
                  size={6}
                  colorSX={theme.palette.primary.main}
                />
              </S.EmptyStateIcon>
              <S.EmptyStateTitle>Como posso te ajudar hoje?</S.EmptyStateTitle>
              <S.EmptyStateSubtitle>
                Sou seu assistente inteligente com IA. Pergunte sobre clientes,
                financeiro, relatórios e obtenha insights do seu negócio
                instantaneamente.
              </S.EmptyStateSubtitle>
              <S.SuggestedQuestions>
                {searchPhrases.slice(0, 4).map((phrase) => (
                  <S.SuggestedQuestion
                    key={phrase}
                    onClick={() => handleSendMessage(phrase)}
                  >
                    {phrase}
                  </S.SuggestedQuestion>
                ))}
              </S.SuggestedQuestions>
            </S.WelcomeHeader>

            {messages.map((msg) => {
              const currentFeedback = (msg.feedback ?? 0) as 0 | 1 | 2
              const loadingFb = !!feedbackLoading[msg.id]
              const showLike = currentFeedback !== FEEDBACK.dislike
              const showDislike = currentFeedback !== FEEDBACK.like

              return (
                <S.MessageContainer key={msg.id} isUser={msg.isUser}>
                  {!msg.isUser && (
                    <S.MessageWrapper isUser={false}>
                      <S.AIAvatar>
                        <Icon
                          icon="autoAwesome"
                          size={2.5}
                          colorSX={theme.palette.primary.main}
                        />
                      </S.AIAvatar>
                      <Box>
                        <S.AgentMessage>
                          {parseMessage(msg.text, handleOpenReportModal)}
                        </S.AgentMessage>

                        <S.MessageActions className="message-actions">
                          {showLike && (
                            <S.ActionButton
                              aria-label="Curtir"
                              onClick={() => handleLike(msg)}
                              disabled={loadingFb}
                            >
                              {currentFeedback === FEEDBACK.like ? (
                                <ThumbUp
                                  sx={{ color: 'black', fontSize: '17px' }}
                                />
                              ) : (
                                <Icon icon="thumbUpAlt" size={2.25} />
                              )}
                            </S.ActionButton>
                          )}
                          {showDislike && (
                            <S.ActionButton
                              aria-label="Não curtir"
                              onClick={() => handleDislike(msg)}
                              disabled={loadingFb}
                            >
                              {currentFeedback === FEEDBACK.dislike ? (
                                <ThumbDown
                                  sx={{ color: 'black', fontSize: '17px' }}
                                />
                              ) : (
                                <Icon icon="thumbDownAlt" size={2.25} />
                              )}
                            </S.ActionButton>
                          )}
                          <S.ActionButton
                            aria-label="Copiar"
                            onClick={() => handleCopy(msg)}
                          >
                            <Icon icon="contentCopy" size={2.25} />
                          </S.ActionButton>
                        </S.MessageActions>
                      </Box>
                    </S.MessageWrapper>
                  )}

                  {msg.isUser && <S.UserMessage>{msg.text}</S.UserMessage>}
                </S.MessageContainer>
              )
            })}

            {isLoading && <AILoadingIndicator />}

            <div ref={messagesEndRef} />
          </S.MessagesArea>

          <S.ScrollDownButton
            visible={showScrollDown}
            onClick={scrollToBottom}
            aria-label="Ir para o fim"
          >
            <Icon
              icon="faArrowDown"
              size={2}
              colorSX={theme.palette.primary.main}
            />
          </S.ScrollDownButton>
          <S.BottomFade visible={showScrollDown} />
        </ModalContent>

        <ModalFooter
          sx={{
            position: 'relative',
            flexDirection: 'column',
            gap: '8px',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '10%',
              right: '10%',
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}30, ${theme.palette.secondary.main}30, transparent)`
            }
          }}
        >
          <S.FooterWrapper>
            <S.ModalInputWrapper sx={{ opacity: isLoading ? 0.6 : 1 }}>
              <S.ModalTextarea
                ref={textAreaRef}
                rows={1}
                placeholder={
                  isLoading
                    ? 'Aguarde a resposta...'
                    : 'Pergunte qualquer coisa sobre seu negócio...'
                }
                value={message}
                atMax={atMax}
                onInput={adjustInputHeight}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setMessage(e.target.value)
                }
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <S.SendIconButton
                onClick={() => handleSendMessage()}
                disabled={!message.trim() || isLoading}
                hasMessage={!!message.trim()}
              >
                {isLoading ? (
                  <CircularProgress
                    size={16}
                    sx={{ color: theme.palette.neutral[100] }}
                  />
                ) : (
                  <Icon
                    icon="send"
                    size={2.25}
                    colorSX={
                      message.trim()
                        ? theme.palette.neutral[100]
                        : theme.palette.neutral[500]
                    }
                  />
                )}
              </S.SendIconButton>
            </S.ModalInputWrapper>
            <S.Disclaimer className="disclaimer">
              <Icon
                icon="info"
                size={1.75}
                colorSX={theme.palette.neutral[450]}
              />
              <span>
                Versão de testes. Podem ocorrer erros nas informações. Avalie as
                respostas usando os botões de curtir ou não curtir.
              </span>
            </S.Disclaimer>
          </S.FooterWrapper>
        </ModalFooter>
      </Modal>

      <CSVModal
        open={csvModalOpen}
        onClose={handleCloseCSVModal}
        csvUrl={csvUrl}
      />

      {/* SC-10920: formulário aberto a partir da conversa, já com o cliente e
          o que o merchant informou. Quem confere e salva é ele. */}
      {uiAction.action?.type === 'open_charge_modal' && (
        <ModalNewCharge
          isOpenModalNewCharge
          onClose={uiAction.close}
          handleCloseModal={uiAction.close}
          defaultValues={uiAction.formDefaults}
        />
      )}
      {uiAction.action?.type === 'open_recurrence_modal' && (
        <ModalNewRecurrence
          isOpenModalNewRecurrence
          onClose={uiAction.close}
          handleCloseModal={uiAction.close}
          defaultValues={uiAction.formDefaults}
        />
      )}

      {/* Loader para carregamento do report */}
      {reportLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress
            size={24}
            sx={{
              color: theme.palette.primary.main
            }}
          />
        </Box>
      )}
    </>
  )
}

export default ChatAssistant
